import React, { useState, useEffect } from 'react';
import KriteriaItem from './kriteria_item';
import MerkbladKriteriaManager from '../beoordelaaradmin/merkblad';
import { getAllKriteria, createKriteria, updateKriteria, deleteKriteria } from '../services/kriteria_services';
import { getPlan } from '../services/rondte_services';

function MerkAdmin() {
  const [kriteriaList, setKriteriaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingKriteria, setEditingKriteria] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newKriteria, setNewKriteria] = useState({
    beskrywing: '',
    default_totaal: 0,
    gids: '' // optional gids field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rondtes, setRondtes] = useState([]);
  const [gekoseRondteId, setGekoseRondteId] = useState(null);
  const [kriteriaWeergawe, setKriteriaWeergawe] = useState(0); // bumped so the per-round manager reloads the kriteria

  // Load kriteria on component mount
  useEffect(() => {
    loadKriteria();
  }, []);

  // Load the rounds, so the admin can pick which round's kriteria to manage
  useEffect(() => {
    getPlan()
      .then(data => {
        setRondtes(data.rondtes);
        const gestarte = data.rondtes.filter(r => r.status !== 'beplan');
        if (gestarte.length > 0) {
          setGekoseRondteId(gestarte[gestarte.length - 1].rondte_id);
        } else if (data.rondtes.length > 0) {
          setGekoseRondteId(data.rondtes[0].rondte_id);
        }
      })
      .catch(err => setError(err.message));
  }, []);

  const gekoseRondte = rondtes.find(r => r.rondte_id === gekoseRondteId) || null;

  const loadKriteria = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllKriteria();
      setKriteriaList(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddKriteria = async (e) => {
    e.preventDefault();
    if (!newKriteria.beskrywing.trim()) {
      alert('Beskrywing is verpligtend');
      return;
    }

    if (newKriteria.default_totaal < 0) {
      alert('Default totaal moet 0 of meer wees');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createKriteria(newKriteria);
      // Add the new kriteria to the list
      const createdKriteria = {
        kriteria_id: result.id,
        beskrywing: newKriteria.beskrywing,
        default_totaal: newKriteria.default_totaal
      };
      setKriteriaList(prev => [...prev, createdKriteria]);
      setNewKriteria({ beskrywing: '', default_totaal: 0, gids: '' }); // reset gids
      setShowAddForm(false);
      setKriteriaWeergawe(prev => prev + 1);
    } catch (err) {
      alert(`Fout: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKriteriaUpdated = async (kriteriaId, updatedData) => {
    try {
      await updateKriteria(kriteriaId, updatedData);
      // Update the kriteria in the list
      setKriteriaList(prev => 
        prev.map(kriteria => 
          kriteria.kriteria_id === kriteriaId 
            ? { ...kriteria, ...updatedData }
            : kriteria
        )
      );
      setEditingKriteria(null);
      setKriteriaWeergawe(prev => prev + 1);
    } catch (err) {
      throw err;
    }
  };

  const handleKriteriaDeleted = async (kriteriaId) => {
    try {
      await deleteKriteria(kriteriaId);
      // Remove the kriteria from the list
      setKriteriaList(prev => 
        prev.filter(kriteria => kriteria.kriteria_id !== kriteriaId)
      );
      setEditingKriteria(null);
      setKriteriaWeergawe(prev => prev + 1);
    } catch (err) {
      throw err;
    }
  };

  const handleEditKriteria = (kriteria) => {
    setEditingKriteria(kriteria);
    setShowAddForm(false);
  };

  const handleCancelEdit = () => {
    setEditingKriteria(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewKriteria(prev => ({
      ...prev,
      [name]: name === 'default_totaal' ? parseFloat(value) || 0 : value
    }));
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>

      
      {error && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          marginBottom: '1rem' 
        }}>
          Fout: {error}
        </div>
      )}

      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0e1e3b', marginBottom: '0.5rem' }}>Kriteria per Rondte</h2>
        <p style={{ color: '#666', marginTop: 0 }}>
          Kies watter kriteria op 'n rondte se merkblad staan en stel die totaal per kriteria.
        </p>
        {rondtes.length === 0 ? (
          <p style={{ color: '#666' }}>Daar is nog geen rondtes nie. Skep 'n plan onder Rondtes.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              {rondtes.map(r => (
                <button
                  key={r.rondte_id}
                  type="button"
                  onClick={() => setGekoseRondteId(r.rondte_id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '20px',
                    border: '2px solid #0e1e3b',
                    backgroundColor: gekoseRondteId === r.rondte_id ? '#0e1e3b' : 'white',
                    color: gekoseRondteId === r.rondte_id ? 'white' : '#0e1e3b',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {r.naam}
                </button>
              ))}
            </div>
            {gekoseRondte && (
              <MerkbladKriteriaManager rondteId={gekoseRondte.rondte_id} refreshKey={kriteriaWeergawe} />
            )}
          </>
        )}
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '2rem 0' }} />

      <h2 style={{ color: '#0e1e3b', marginBottom: '0.5rem' }}>Alle Kriteria</h2>

      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          disabled={editingKriteria !== null}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#0e1e3b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: editingKriteria ? 'not-allowed' : 'pointer',
            opacity: editingKriteria ? 0.6 : 1,
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          {showAddForm ? 'Kanselleer Nuwe Kriteria' : 'Skep Nuwe Kriteria'}
        </button>
      </div>

      {showAddForm && (
        <div style={{
          marginBottom: '2rem',
          padding: '1rem',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9'
        }}>
          <h3>Skep Nuwe Kriteria</h3>
          <form onSubmit={handleAddKriteria}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="new-beskrywing" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Beskrywing *
              </label>
              <input
                type="text"
                id="new-beskrywing"
                name="beskrywing"
                value={newKriteria.beskrywing}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="Voer kriteria beskrywing in"
              />
            </div>
            {/* Optional gids input */}
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="new-gids" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Gids (opsioneel)
              </label>
              <input
                type="text"
                id="new-gids"
                name="gids"
                value={newKriteria.gids}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="Gids waarde (opsioneel)"
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="new-default-totaal" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Default Totaal *
              </label>
              <input
                type="number"
                id="new-default-totaal"
                name="default_totaal"
                value={newKriteria.default_totaal}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                required
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
                placeholder="0.00"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#0e1e3b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.6 : 1
                }}
              >
                {isSubmitting ? 'Skep...' : 'Skep Kriteria'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewKriteria({ beskrywing: '', default_totaal: 0, gids: '' });
                }}
                disabled={isSubmitting}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#77502d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                Kanselleer
              </button>
            </div>
          </form>
        </div>
      )}

      {editingKriteria && (
        <KriteriaItem
          kriteria={editingKriteria}
          onKriteriaUpdated={handleKriteriaUpdated}
          onKriteriaDeleted={handleKriteriaDeleted}
          onCancel={handleCancelEdit}
        />
      )}

      <div>
        <h2 style={{ color: '#0e1e3b', marginBottom: '1rem' }}>
          Bestaande Kriteria ({kriteriaList.length})
        </h2>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Laai kriteria...
          </div>
        ) : kriteriaList.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            Geen kriteria gevind nie. Skep die eerste kriteria hierbo.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {kriteriaList.map(kriteria => (
              <div
                key={kriteria.kriteria_id}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: editingKriteria?.kriteria_id === kriteria.kriteria_id ? '#e3f2fd' : 'white',
                  cursor: editingKriteria?.kriteria_id === kriteria.kriteria_id ? 'default' : 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => editingKriteria?.kriteria_id !== kriteria.kriteria_id && handleEditKriteria(kriteria)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#0e1e3b' }}>
                      {kriteria.beskrywing}
                    </h4>
                    <p style={{ margin: '0', color: '#666' }}>
                      Default Totaal: <strong>{kriteria.default_totaal}</strong>
                    </p>
                  </div>
                  {editingKriteria?.kriteria_id !== kriteria.kriteria_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditKriteria(kriteria);
                      }}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#77502d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Wysig
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MerkAdmin;